import { Floorplan } from "@domain/project/floorplan/floorplan";
import { FloorplanEventType } from "@domain/project/floorplan/floorplan-event";
import { FloorplanItem } from "@domain/project/floorplan/floorplan-item";
import { FloorplanPlaceholder } from "@domain/project/floorplan/floorplan-placeholder";
import { FloorplanPlaceholderPositionKind } from "@domain/project/floorplan/floorplan-placeholder-position-kind";
import { FloorplanRemoteSensor } from "@domain/project/floorplan/floorplan-remote-sensor";
import { IsInstanceOf } from "@utils/class-validator/class-validator-constraints";
import { Exclude, Expose, Type } from "class-transformer";
import { IsBoolean, IsNumber, IsOptional, IsPositive, IsString } from "class-validator";
import { Subject } from "rxjs";
import { v4 as uuidv4 } from "uuid";

export class FloorplanTransmitterPlaceholder extends FloorplanPlaceholder {
  @Exclude()
  DEFAULT_MONITORING_AREA_RADIUS: number = 2.5;

  @IsNumber()
  @IsPositive({ message: "The value must be higher than 0" })
  @Expose({ name: "monitoringAreaRadius" })
  private _monitoringAreaRadius: number;

  @IsString()
  @IsOptional()
  @Expose({ name: "bmk" })
  private _bmk?: string;

  @IsString()
  @IsOptional()
  @Expose({ name: "sector" })
  private _sector?: string;

  @IsString()
  @IsOptional()
  @Expose({ name: "installationHeight" })
  private _installationHeight?: string;

  @IsBoolean()
  @IsOptional()
  @Expose({ name: "exProtection" })
  private _exProtection?: boolean;

  @IsInstanceOf(FloorplanRemoteSensor)
  @IsOptional()
  @Type(() => FloorplanRemoteSensor)
  @Expose({ name: "remoteSensor" })
  private _remoteSensor?: FloorplanRemoteSensor;

  @IsNumber()
  private _referenceMonitoringAreaRadius: number = this.DEFAULT_MONITORING_AREA_RADIUS;

  @Exclude()
  public monitoringAreaRadiusChanged: Subject<{ radius: number }>;

  @Exclude()
  referenceMonitoringAreaRadiusChanged: Subject<{ referenceLength: number }>;

  constructor(
    floorplan: Floorplan,
    id: string,
    x: number,
    y: number,
    size: number,
    monitoringAreaRadius: number,
    notes?: string,
  ) {
    super(floorplan, id, x, y, size, notes);
    this._monitoringAreaRadius = monitoringAreaRadius;
    this._remoteSensor = undefined;
    this.monitoringAreaRadiusChanged = new Subject();
    this.referenceMonitoringAreaRadiusChanged = new Subject();
    this.updateMonitoringAreaRadiusByReferenceMeasure();
  }

  setItemTextVisibility() {
    this.itemTextVisible = this._floorplan.floorplanState.transmittersItemTextVisible;
  }

  override init(floorplan: Floorplan) {
    super.init(floorplan);
    if (this._remoteSensor) {
      this._remoteSensor.init(floorplan, this);
    }
  }

  protected override createRawDuplicate(): ((x: number, y: number) => FloorplanItem) | undefined {
    const monitoringAreaRadius = this.monitoringAreaRadius;
    const size = this.size;

    return (x: number, y: number) => FloorplanTransmitterPlaceholder.create(this._floorplan, x, y, size, monitoringAreaRadius);
  }

  protected override copySpecificDuplicateStateTo(): ((duplicate: FloorplanItem) => void) | undefined {
    const bmk = this.bmk;
    const exProtection = this.exProtection;
    const installationHeight = this.installationHeight;
    const products = this.capturePlaceholderProducts();
    const sector = this.sector;

    return (duplicate: FloorplanItem) => {
      const duplicatePlaceholder = duplicate as FloorplanTransmitterPlaceholder;
      this.copyPlaceholderProductsTo(duplicatePlaceholder, products);
      duplicatePlaceholder._bmk = bmk;
      duplicatePlaceholder._sector = sector;
      duplicatePlaceholder._installationHeight = installationHeight;
      duplicatePlaceholder._exProtection = exProtection;
    };
  }

  protected override afterDuplicateAdded(): ((duplicate: FloorplanItem) => void) | undefined {
    const afterDuplicateAdded = super.afterDuplicateAdded();
    const referenceMonitoringAreaRadius = this.referenceMonitoringAreaRadius;
    const hasRemoteSensor = !!this.remoteSensor;

    return (duplicate: FloorplanItem) => {
      const duplicatePlaceholder = duplicate as FloorplanTransmitterPlaceholder;
      afterDuplicateAdded?.(duplicatePlaceholder);

      if (this._floorplan.referenceMeasure) {
        duplicatePlaceholder.updateMonitoringAreaRadius(referenceMonitoringAreaRadius);
      }

      if (hasRemoteSensor) {
        duplicatePlaceholder.attachRemoteSensor();
      }
    };
  }

  override getDuplicateTargetCollection() {
    return this._floorplan.transmitterPlaceholders;
  }

  protected override registerDuplicate() {
    return (duplicate: FloorplanItem, afterDuplicateAdded?: (duplicate: FloorplanItem) => void) =>
      this.registerDuplicateInFloorplan(
        duplicate as FloorplanTransmitterPlaceholder,
        this._floorplan.transmitterPlaceholders,
        FloorplanEventType.TRANSMITTER_PLACEHOLDER_ADDED,
        () => this.refreshPlaceholderPositionIds(),
        afterDuplicateAdded as ((duplicate: FloorplanTransmitterPlaceholder) => void) | undefined,
      );
  }

  override delete() {
    this.monitoringAreaRadiusChanged.unsubscribe();
    this._floorplan.deleteTransmitterPlaceholder(this);
  }

  get monitoringAreaRadius(): number {
    return this._monitoringAreaRadius;
  }

  set monitoringAreaRadius(value: number) {
    if (this._floorplan.referenceMeasure) {
      value = this.calculateMonitoringArea();
    }
    this._monitoringAreaRadius = value;
    this._floorplan.publishUpdate(FloorplanEventType.ITEM_UPDATED, this);
  }

  get referenceMonitoringAreaDiameter(): number {
    return this._referenceMonitoringAreaRadius * 2;
  }

  set referenceMonitoringAreaDiameter(value: number) {
    this._monitoringAreaRadius = value / 2;
  }

  get referenceMonitoringAreaRadius() {
    return this._referenceMonitoringAreaRadius;
  }

  get bmk(): string | undefined {
    return this._bmk;
  }

  set bmk(value: string) {
    this._bmk = value;
  }

  get sector(): string | undefined {
    return this._sector;
  }

  set sector(value: string) {
    this._sector = value;
  }

  get installationHeight(): string | undefined {
    return this._installationHeight;
  }

  set installationHeight(value: string) {
    this._installationHeight = value;
  }

  get exProtection(): boolean | undefined {
    return this._exProtection;
  }

  set exProtection(value: boolean) {
    this._exProtection = value;
  }

  isReferenceMeasureSet() {
    if (this._floorplan) return !!this._floorplan.referenceMeasure;
    return false;
  }

  updateMonitoringAreaRadius(radius: number) {
    if (this.isReferenceMeasureSet()) {
      this._referenceMonitoringAreaRadius = radius;
      this._monitoringAreaRadius = radius * this._floorplan.getReferenceScale();
    }
    this._floorplan.publishUpdate(FloorplanEventType.MONITORING_AREA_RADIUS_UPDATED, this);
  }

  updateMonitoringAreaRadiusByReferenceMeasure(): void {
    if (this.isReferenceMeasureSet()) {
      this._monitoringAreaRadius = this.calculateMonitoringAreaByReference();
      this._floorplan.publishUpdate(FloorplanEventType.MONITORING_AREA_RADIUS_UPDATED, this);
    }
  }

  get remoteSensor(): FloorplanRemoteSensor | undefined {
    return this._remoteSensor;
  }

  set remoteSensor(value: FloorplanRemoteSensor) {
    this._remoteSensor = value;
  }

  attachRemoteSensor() {
    this._remoteSensor = new FloorplanRemoteSensor(this._floorplan, this, uuidv4(), this.x, this.y + 2 * this.size, this.size);
    this._floorplan.publishUpdate(FloorplanEventType.REMOTE_SENSOR_ATTACHED, this);
  }

  detachRemoteSensor() {
    this._remoteSensor = undefined;
    this._floorplan.publishUpdate(FloorplanEventType.REMOTE_SENSOR_DETACHED, this);
  }

  public isMonitoringAreaScaleAble(): boolean {
    return !this._floorplan.referenceMeasure;
  }

  protected override refreshFloorplanLockState() {
    this._floorplan.floorplanState.refreshTransmittersLock();
  }

  override getPlaceholderPositionKind(): FloorplanPlaceholderPositionKind | undefined {
    return FloorplanPlaceholderPositionKind.TRANSMITTER;
  }

  static create(
    floorplan: Floorplan,
    x: number,
    y: number,
    size: number,
    monitoringAreaRadius: number,
  ): FloorplanTransmitterPlaceholder {
    return new FloorplanTransmitterPlaceholder(floorplan, uuidv4(), x, y, size, monitoringAreaRadius);
  }

  private calculateMonitoringAreaByReference(): number {
    return Math.round(this.referenceMonitoringAreaRadius * this._floorplan.getReferenceScale() * 100) / 100;
  }

  private calculateMonitoringArea(): number {
    return Math.round((this.monitoringAreaRadius / this._floorplan.getReferenceScale()) * 100) / 100;
  }
}
